import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { DetailRow, fmt, fmtDate, type TypedData } from "./meter-info-utils";

export {
	DetailRow,
	fmt,
	fmtDate,
	StatCard,
	type TypedData,
} from "./meter-info-utils";
export { SourcesCard } from "./sources-card";

export function MeterInfoCards({
	sn: _sn,
	typed,
	isLoading,
}: {
	sn: string;
	typed: TypedData | undefined;
	isLoading: boolean;
}) {
	const { t } = useTranslation();
	const d = typed?.data;

	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
			<Card>
				<CardHeader className="pb-2">
					<CardTitle className="text-base">
						{t("meterInfo.basicData")}
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-0">
					{isLoading ? (
						Array.from({ length: 5 }).map((_, i) => (
							<Skeleton key={i} className="my-1 h-4 w-full" />
						))
					) : (
						<>
							<DetailRow
								label={t("meterInfo.serialNumber")}
								value={d?.serial_number}
							/>
							{typed?.kind === "woda" && (
								<DetailRow
									label={t("meterInfo.manufactureDate")}
									value={fmtDate(typed.data.manufacture_date)}
								/>
							)}
							<DetailRow
								label={t("meterInfo.legalizationDate")}
								value={fmtDate(d?.legalization_date)}
							/>
							<DetailRow
								label={t("meterInfo.manufacturer")}
								value={d?.manufacturer?.name ?? "—"}
							/>
							<DetailRow
								label={t("meterInfo.district")}
								value={d?.district?.name}
							/>
							<DetailRow
								label={t("meterInfo.apartment")}
								value={
									d?.apartment
										? `${d.apartment.street} ${d.apartment.apartment_number}`
										: "—"
								}
							/>
						</>
					)}
				</CardContent>
			</Card>

			<Card>
				<CardHeader className="pb-2">
					<CardTitle className="text-base">
						{t("meterInfo.communication")}
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-0">
					{isLoading ? (
						Array.from({ length: 4 }).map((_, i) => (
							<Skeleton key={i} className="my-1 h-4 w-full" />
						))
					) : (
						<>
							<DetailRow
								label={t("meterInfo.lastCommunication")}
								value={fmt(d?.last_communication)}
							/>
							<DetailRow
								label={t("meterInfo.prevCommunication")}
								value={fmt(d?.previous_communication)}
							/>
							{typed?.kind === "energia" && (
								<>
									<DetailRow
										label={t("meterInfo.voltageL1")}
										value={
											typed.data.last_voltage_p1 != null
												? `${typed.data.last_voltage_p1} V`
												: "—"
										}
									/>
									<DetailRow
										label={t("meterInfo.voltageL2")}
										value={
											typed.data.last_voltage_p2 != null
												? `${typed.data.last_voltage_p2} V`
												: "—"
										}
									/>
									<DetailRow
										label={t("meterInfo.voltageL3")}
										value={
											typed.data.last_voltage_p3 != null
												? `${typed.data.last_voltage_p3} V`
												: "—"
										}
									/>
									<DetailRow
										label={t("meterInfo.productionPower")}
										value={
											typed.data.last_power_production != null
												? `${typed.data.last_power_production} W`
												: "—"
										}
									/>
								</>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
